import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import { parseDate } from '../../lib/utils';
import { FiSearch, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';

// Compose a directory record's premise address into the single-line `address`
// field the contact form uses.
export function formatDirectoryAddress(rec) {
  if (!rec) return '';
  const cityState = [rec.premise_city, rec.premise_state].filter(Boolean).join(', ');
  return [rec.premise_street, cityState, rec.premise_zip].filter(Boolean).join(', ');
}

// Map a national-directory record onto the DEALER contact fields. The contact
// stays a tenant-owned copy; `ffl_directory_number` + `ffl_last_verified_at`
// record the soft link so it can be re-verified later. Staff can still edit any
// field after autofill.
export function directoryRecordToContact(rec) {
  return {
    type: 'DEALER',
    name: rec.business_name || rec.license_name || '',
    contact_name: rec.license_name || '',
    ffl_number: rec.ffl_dashed || rec.ffl_number || '',
    ffl_expiration_date: rec.expiration_date || '',
    phone: rec.voice_phone || '',
    address: formatDirectoryAddress(rec),
    ffl_directory_number: rec.ffl_number || null,
    ffl_last_verified_at: new Date().toISOString(),
  };
}

function expiryLabel(rec) {
  if (!rec.expiration_date) return null;
  const exp = parseDate(rec.expiration_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expired = exp < today;
  return (
    <span className={expired ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}>
      {expired ? 'Expired' : 'Exp'} {exp.toLocaleDateString()}
    </span>
  );
}

/**
 * Search-and-pick against the national ATF FFL directory. On pick, calls
 * `onSelect(record)` — the parent maps it onto its form via
 * `directoryRecordToContact`.
 */
export default function FflDirectoryLookup({ onSelect, label = 'Look up FFL (ATF directory)' }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Debounced directory search.
  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await api.searchFflDirectory(term, 20);
        if (!cancelled) {
          setResults(data);
          setOpen(true);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const pick = (rec) => {
    onSelect?.(rec);
    setOpen(false);
    setSearch('');
    setResults([]);
  };

  return (
    <div className="w-full" ref={containerRef}>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {label}
      </label>
      <div className="relative">
        <SafeIcon
          icon={FiSearch}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
        />
        <input
          type="text"
          value={search}
          placeholder="FFL number, business name, or city, state…"
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-900 dark:border-slate-700 dark:text-white"
        />

        {open && (
          <ul className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg dark:bg-slate-900 dark:border-slate-700">
            {loading && <li className="px-3 py-2 text-slate-400">Searching ATF directory…</li>}
            {!loading && results.length === 0 && (
              <li className="px-3 py-2 text-slate-400">No matching FFLs found</li>
            )}
            {!loading && results.map((rec) => (
              <li
                key={rec.ffl_number}
                onClick={() => pick(rec)}
                className="cursor-pointer px-3 py-2 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">
                      {rec.business_name || rec.license_name || rec.ffl_dashed}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {[rec.ffl_dashed, [rec.premise_city, rec.premise_state].filter(Boolean).join(', ')]
                        .filter(Boolean)
                        .join(' • ')}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-[11px]">
                    {expiryLabel(rec)}
                    {rec.is_active ? (
                      <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-green-500" title="Active in latest ATF list" />
                    ) : (
                      <SafeIcon icon={FiAlertTriangle} className="h-4 w-4 text-amber-500" title="Not in latest ATF list" />
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Pulls name, FFL number, address &amp; expiration from the ATF list. You can edit any field after.
      </p>
    </div>
  );
}
